/**
 * All of the page's copy and links in one place. Figures, prices and wording
 * are taken from littlenap.in — update them here when the site changes.
 */

export const SITE = 'https://www.littlenap.in';
export const PHONE = { label: '+91 88828 00600', href: 'tel:+918882800600' };
export const PHONE_TN = { label: '+91 94432 12345', href: 'tel:+919443212345' };

export interface NavLink {
  label: string;
  href: string;
  /** a page on littlenap.in rather than a section of this one: opens in a new tab */
  external?: boolean;
}

/**
 * The links in the header, after its Collections menu (which lists the
 * categories of RANGE below, so it needs no entry here).
 */
export const NAV: NavLink[] = [
  { label: 'View in AR', href: '#ar' },
  { label: 'Best Sellers', href: '#bestsellers' },
  { label: 'Videos', href: '#films' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Our Story', href: '#story' },
  { label: 'Franchise', href: '#franchise' },
];

/** The shop's account pages on littlenap.in. */
export const ACCOUNT = { login: `${SITE}/customer/login`, cart: `${SITE}/cart` };

export interface Stat {
  value: string;
  label: string;
}

export const STATS: Stat[] = [
  { value: '15', label: 'Years of delivering comfort' },
  { value: '21,000+', label: 'Happy customers' },
  { value: '1,300+', label: 'Cinema screens' },
  { value: '18+', label: 'Showrooms' },
  { value: '470+', label: 'Team members' },
  { value: '2', label: 'Manufacturing units' },
];

/** One card in the range browser: a product, a model, or a segment. */
export interface RangeItem {
  name: string;
  /** the small line above the name */
  type: string;
  image: string;
  alt: string;
  href: string;
  /** photographed on a plain backdrop: show the whole picture instead of cropping it to fill the card */
  cutout?: boolean;
  /** that backdrop's colour, when it is not white */
  backdrop?: string;
  /** one line about it, shown when the site lists no price */
  text?: string;
  price?: string;
  mrp?: string;
  off?: string;
  /** stars out of five and the number of reviews on its listing — left out when it has none */
  rating?: { stars: number; reviews: number };
  /**
   * The dots on a small card: the finish in the picture, then three more. Apart from the first
   * these are sample colours, not the model's real options — replace them when those are known.
   */
  colours?: string[];
}

export interface RangeTab {
  id: string;
  label: string;
  /** the link under the cards */
  more: { label: string; href: string };
  /**
   * The first is the large card — give it a wide picture — and the next two are the small cards
   * beside it. Any further ones are not shown: the link under the cards leads to the whole list.
   */
  items: RangeItem[];
  /** shown in place of cards when the site lists nothing to buy in this category yet */
  banner?: { title: string; text: string; image: string; alt: string; points: string[] };
}

/**
 * The corner of the range's large card: a few faces beside the number of happy customers (the
 * figure in STATS). The portraits are placeholders from randomuser.me — replace them with real
 * customers' pictures before the page goes live.
 */
export const FANS = {
  faces: ['assets/misc/avatar-1.jpg', 'assets/misc/avatar-2.jpg', 'assets/misc/avatar-3.jpg'],
  count: '21k+',
  label: 'Happy customers',
};

/**
 * The range, one tab per category in littlenap.in's menu. Names, prices,
 * ratings and descriptions are the site's own (read on 2 October 2026):
 *  - Living Room comes from the living-room listing.
 *  - Home Theater, Multiplex and part of Lounger come from the Cinema Comfort
 *    Collection, which both the home-theater and the multiplex page send
 *    visitors to. The site gives those models no price.
 *  - The site's Massage Chair category currently lists no products.
 */
export const RANGE: RangeTab[] = [
  {
    id: 'living',
    label: 'Living Room',
    more: { label: 'View all living room recliners', href: `${SITE}/living-room-recliners` },
    items: [
      {
        name: 'Avion',
        type: 'Single seater',
        image: 'assets/range/avion.webp',
        alt: 'Avion recliner',
        href: `${SITE}/living-room-recliners/avion-recliner`,
        price: '₹21,000',
        mrp: '₹34,999',
        off: '40% off',
        rating: { stars: 4, reviews: 11 },
      },
      {
        name: 'Otium',
        type: 'Manual · Single seater',
        image: 'assets/products/otium.jpg',
        alt: 'Otium recliner',
        href: `${SITE}/living-room-recliners/otium-recliner-manual-single-seater`,
        price: '₹36,550',
        mrp: '₹43,000',
        off: '15% off',
        rating: { stars: 4, reviews: 5 },
        colours: ['#1f1c1b', '#6b3a2a', '#cdbb9f', '#7d2228'],
      },
      {
        name: 'Swift',
        type: 'Manual · Single seater',
        image: 'assets/products/swift.jpg',
        alt: 'Swift recliner',
        href: `${SITE}/living-room-recliners/swift-recliner-manual-single-seater`,
        price: '₹36,550',
        mrp: '₹43,000',
        off: '15% off',
        rating: { stars: 4, reviews: 5 },
        colours: ['#d8c6a9', '#8b8e92', '#30495b', '#6b3a2a'],
      },
      {
        name: 'Quotium',
        type: 'Manual · Single seater',
        image: 'assets/products/quotium.jpg',
        alt: 'Quotium recliner',
        href: `${SITE}/living-room-recliners/quotium-black-recliner-manual-single-seater`,
        price: '₹36,550',
        mrp: '₹43,000',
        off: '15% off',
        rating: { stars: 5, reviews: 1 },
      },
    ],
  },
  {
    id: 'theater',
    label: 'Home Theater',
    more: { label: 'View the Cinema Comfort Collection', href: `${SITE}/our-collection` },
    items: [
      {
        name: 'Gatsby',
        type: 'Cinema Comfort · Premium',
        image: 'assets/range/gatsby.webp',
        alt: 'Gatsby two-seater home theater recliner',
        href: `${SITE}/home-theater-recliners/gatsby-recliner`,
        cutout: true,
        text: 'An exclusive design with technology-driven features.',
      },
      {
        name: 'Trident',
        type: 'Cinema Comfort · Premium',
        image: 'assets/range/trident.webp',
        alt: 'Trident home theater recliner in blue-grey',
        href: `${SITE}/home-theater-recliners/trident`,
        cutout: true,
        text: 'Inspired by premium automobile seats — made entirely of PU foam, with a bucket seat.',
        colours: ['#8d98b0', '#1f1c1b', '#7d2228', '#b98a5e'],
      },
      {
        name: 'Zenith',
        type: 'Cinema Comfort · Premium',
        image: 'assets/range/zenith.webp',
        alt: 'Zenith home theater recliner in red',
        href: `${SITE}/home-theater-recliners/zenith`,
        cutout: true,
        text: 'Best-in-class comfort — completely made of PU foam, with a bucket seat.',
        colours: ['#8f2531', '#1f1c1b', '#b98a5e', '#8d98b0'],
      },
      {
        name: 'Fortuna',
        type: 'Cinema Comfort · Premium',
        image: 'assets/range/fortuna.webp',
        alt: 'Fortuna two-seater home theater recliner in tan',
        href: `${SITE}/home-theater-recliners/fortuna-recliner`,
        cutout: true,
        text: 'An exclusive design with technology-driven features.',
      },
    ],
  },
  {
    id: 'lounger',
    label: 'Lounger',
    more: { label: 'View all loungers', href: `${SITE}/lounger` },
    items: [
      {
        name: 'Repose Lounger',
        type: 'Front-row lounger',
        image: 'assets/range/repose-lounger.webp',
        alt: 'Repose Lounger in a home theater',
        href: `${SITE}/lounger`,
        text: 'Best-in-class foam cushioning and adjustable loose cushions, for long movie marathons.',
      },
      {
        name: 'Regalia Lounger',
        type: 'Lounger',
        image: 'assets/range/regalia-lounger.webp',
        alt: 'Regalia Lounger with a chaise',
        href: `${SITE}/lounger`,
        text: 'A simple, elegant design with a PU back and seat for luxury and support.',
        colours: ['#e3dbce', '#8b8e92', '#3a3a3c', '#b98a5e'],
      },
      {
        name: 'Primus Lounger',
        type: 'Lounger',
        image: 'assets/range/primus-lounger.webp',
        alt: 'Pair of green Primus Loungers',
        href: `${SITE}/lounger`,
        text: 'A multipurpose lounger shaped for the different postures of your day.',
        colours: ['#3f6b4f', '#1f1c1b', '#7d2228', '#cdbb9f'],
      },
      {
        name: 'Lunar Lounger',
        type: 'Motorised lounger',
        image: 'assets/range/lunar-lounger.webp',
        alt: 'Lunar motorised lounger in cream',
        href: `${SITE}/home-theater-recliners/lunar-lounger`,
        cutout: true,
        text: 'A liftable headrest, a premium finish and a touch of metal.',
      },
    ],
  },
  {
    id: 'multiplex',
    label: 'Multiplex',
    more: { label: 'Explore multiplex recliners', href: `${SITE}/multiplex-recliners` },
    items: [
      {
        name: 'Aderia',
        type: 'Cinema Comfort Collection',
        image: 'assets/range/aderia.webp',
        alt: 'Aderia two-seater cinema recliner with swivel tables',
        href: `${SITE}/home-theater-recliners/aderia`,
        cutout: true,
        text: 'A bestseller: double-needle stitching, contrast piping, a double swivel table and touch-sensor control.',
      },
      {
        name: 'Millor – NX',
        type: 'Cinema Comfort Collection',
        image: 'assets/range/millor-nx.webp',
        alt: 'Millor NX cinema recliner in brown',
        href: `${SITE}/home-theater-recliners/millor-nx`,
        cutout: true,
        backdrop: '#ffefed',
        text: 'A distinctive three-layered back — the timeless Millor style with modern technology.',
        colours: ['#5a3527', '#1f1c1b', '#8f2531', '#cdbb9f'],
      },
      {
        name: 'Nuvo',
        type: 'Cinema Comfort Collection',
        image: 'assets/range/nuvo.webp',
        alt: 'Nuvo two-seater cinema recliner in red',
        href: `${SITE}/home-theater-recliners/nuvo`,
        cutout: true,
        text: 'Inspired by premium automobile seats — made in PU foam, with a bucket seat.',
        colours: ['#a5343b', '#1f1c1b', '#b98a5e', '#5a3527'],
      },
      {
        name: 'Ritz',
        type: 'Cinema Comfort Collection',
        image: 'assets/range/ritz.webp',
        alt: 'Ritz two-seater cinema recliner in black',
        href: `${SITE}/home-theater-recliners/ritz`,
        cutout: true,
        text: 'Graceful looks, with a cushioned seat and arms for the utmost comfort.',
      },
    ],
  },
  {
    id: 'segments',
    label: 'Segments',
    more: { label: 'See our projects', href: `${SITE}/our-projects` },
    items: [
      {
        name: 'Hospitals',
        type: 'Hospital seating',
        image: 'assets/range/hospitals.webp',
        alt: 'Patient resting in a hospital recliner',
        href: `${SITE}/hospitals`,
        text: 'Recliners crafted for comfort and support in hospital environments.',
      },
      {
        name: 'Club Houses',
        type: 'Elite clubhouse recliners',
        image: 'assets/range/club-houses.webp',
        alt: 'Clubhouse screening room with red recliners',
        href: `${SITE}/club-houses`,
        text: 'Comfortable and stylish recliners for your clubhouse retreat.',
      },
      {
        name: 'Automobiles',
        type: 'Luxury recliners',
        image: 'assets/range/automobiles.webp',
        alt: 'Recliner seats inside a luxury coach',
        href: `${SITE}/automobiles`,
        text: 'Luxury seating solutions for relaxation on the move.',
      },
    ],
  },
  {
    id: 'massage',
    label: 'Massage Chair',
    more: { label: 'Visit the massage chair page', href: `${SITE}/massage-chair` },
    items: [],
    banner: {
      title: 'Massage was never this tempting and gratifying',
      text: 'From foot and calf massagers to full-body chairs — bring spa-like relief home. Tell us what you are looking for and our team will guide you.',
      image: 'assets/collections/massage-chair.webp',
      alt: 'Woman relaxing in a massage chair',
      points: ['3D and 4D massage rollers', 'Zero gravity positioning', 'Heat therapy'],
    },
  },
];

/** Options for the enquiry form's "I'm interested in" list. */
export const INTERESTS = [
  'Living Room Recliners',
  'Home Theater Recliners',
  'Multiplex Recliners',
  'Massage Chair',
  'Recliner Accessories',
  'La-Z-Boy',
];

export type FinderQuestionId = 'place' | 'seats' | 'feature' | 'fabric' | 'budget';

/** One answer to a Recliner Finder question. */
export interface FinderOption {
  /** what is stored, and matched against what each product suits */
  value: string;
  label: string;
  /** a smaller second line */
  note?: string;
  /** draws a sofa outline with this many seats beside the label */
  seats?: 1 | 2 | 3;
}

export interface FinderQuestion {
  id: FinderQuestionId;
  title: string;
  hint: string;
  /** how much a matching answer counts when the products are ranked */
  weight: number;
  options: FinderOption[];
}

/**
 * The Recliner Finder's questions, one per step. Two answers are special:
 * "any" suits every product, and "unsure" (like a skipped question) rules
 * nothing out.
 */
export const FINDER_QUESTIONS: FinderQuestion[] = [
  {
    id: 'place',
    title: 'Where will your recliner go?',
    hint: 'This narrows the range more than anything else.',
    weight: 3,
    options: [
      { value: 'living', label: 'Living room' },
      { value: 'theater', label: 'Home theatre' },
      { value: 'lounge', label: 'Lounge / club house' },
      { value: 'commercial', label: 'Commercial space', note: 'Multiplex, hospital, automobile' },
    ],
  },
  {
    id: 'seats',
    title: 'How many people will use it?',
    hint: 'Pick the seating layout you have room for.',
    weight: 2,
    options: [
      { value: 'single', label: 'Just me', seats: 1 },
      { value: 'double', label: 'Two of us', seats: 2 },
      { value: 'family', label: 'Family (3+)', seats: 3 },
    ],
  },
  {
    id: 'feature',
    title: 'What matters most to you?',
    hint: 'Choose the one you would not give up.',
    weight: 3,
    options: [
      { value: 'manual', label: 'Manual recline', note: 'Simple, no power needed' },
      { value: 'motorised', label: 'Motorised recline', note: 'At the touch of a button' },
      { value: 'massage', label: 'Massage function' },
      { value: 'space', label: 'Space-saving', note: 'Sits close to the wall' },
    ],
  },
  {
    id: 'fabric',
    title: 'Preferred upholstery?',
    hint: 'All three are available across most ranges.',
    weight: 1,
    options: [
      { value: 'leather', label: 'Leather' },
      { value: 'leatherette', label: 'Leatherette' },
      { value: 'fabric', label: 'Fabric' },
      { value: 'any', label: 'No preference' },
    ],
  },
  {
    id: 'budget',
    title: 'What budget do you have in mind?',
    hint: 'Optional - skip it and we will show the full range.',
    weight: 2,
    options: [
      { value: 'under50', label: 'Under ₹50,000' },
      { value: '50to100', label: '₹50,000 – ₹1,00,000' },
      { value: 'above100', label: 'Above ₹1,00,000' },
      { value: 'unsure', label: 'Not sure yet' },
    ],
  },
];

/** One suggestion of the Recliner Finder's results step. */
export interface FinderProduct {
  name: string;
  /** the small line above the name */
  category: string;
  image: string;
  href: string;
  price: string;
  /** the answers it suits, by question */
  suits: Record<FinderQuestionId, string[]>;
}

/**
 * What the Recliner Finder can suggest, in the order ties are broken. The
 * prices are indicative placeholders, NOT littlenap.in's — the results step
 * says so on screen. Replace this list (or the matching in Finder) with the
 * shop's own data when there is a backend to ask.
 */
export const FINDER_PRODUCTS: FinderProduct[] = [
  {
    name: 'Nirvana',
    category: 'Living Room Recliner',
    image: 'assets/finder/nirvana.webp',
    href: `${SITE}/living-room-recliners`,
    price: '₹42,000',
    suits: {
      place: ['living'],
      seats: ['single', 'double'],
      feature: ['manual'],
      fabric: ['fabric', 'leatherette'],
      budget: ['under50', '50to100'],
    },
  },
  {
    name: 'Cresent',
    category: 'Home Theater Recliner',
    image: 'assets/finder/cresent.webp',
    href: `${SITE}/home-theater-recliners`,
    price: '₹78,000',
    suits: {
      place: ['theater'],
      seats: ['double', 'family'],
      feature: ['motorised'],
      fabric: ['leather', 'leatherette'],
      budget: ['50to100', 'above100'],
    },
  },
  {
    name: 'Greyson',
    category: 'La-Z-Boy Recliner',
    image: 'assets/finder/greyson.webp',
    href: `${SITE}/lazboy`,
    price: '₹1,25,000',
    suits: {
      place: ['living', 'lounge'],
      seats: ['single'],
      feature: ['manual', 'space'],
      fabric: ['leather'],
      budget: ['above100'],
    },
  },
  {
    name: 'Cinema Seating',
    category: 'Multiplex Recliner',
    image: 'assets/finder/cinema-seating.webp',
    href: `${SITE}/multiplex-recliners`,
    price: 'On request',
    suits: {
      place: ['commercial'],
      seats: ['family', 'double'],
      feature: ['manual', 'motorised'],
      fabric: ['leatherette'],
      budget: ['50to100', 'above100'],
    },
  },
  {
    name: 'Relaxation Massage Chair',
    category: 'Massage Chair',
    image: 'assets/collections/massage.jpg',
    href: `${SITE}/massage-chair`,
    price: '₹1,80,000',
    suits: {
      place: ['living', 'lounge'],
      seats: ['single'],
      feature: ['massage'],
      fabric: ['leatherette'],
      budget: ['above100'],
    },
  },
  {
    name: 'Classic Lounger',
    category: 'Lounger',
    image: 'assets/finder/classic-lounger.webp',
    href: `${SITE}/lounger`,
    price: '₹35,000',
    suits: {
      place: ['lounge', 'living'],
      seats: ['single'],
      feature: ['manual', 'space'],
      fabric: ['fabric'],
      budget: ['under50'],
    },
  },
  {
    name: 'Family Recliner Sofa',
    category: 'Living Room Recliner',
    image: 'assets/finder/family-recliner-sofa.webp',
    href: `${SITE}/living-room-recliners`,
    price: '₹95,000',
    suits: {
      place: ['living'],
      seats: ['family'],
      feature: ['motorised'],
      fabric: ['fabric', 'leatherette'],
      budget: ['50to100'],
    },
  },
  {
    name: 'Powered Theatre Recliner',
    category: 'Home Theater Recliner',
    image: 'assets/finder/powered-theatre-recliner.webp',
    href: `${SITE}/home-theater-recliners`,
    price: '₹1,45,000',
    suits: {
      place: ['theater', 'commercial'],
      seats: ['double', 'family'],
      feature: ['motorised', 'space'],
      fabric: ['leather'],
      budget: ['above100'],
    },
  },
];

/** One chair of the AR section: a picture for its box, and a 3D model the visitor can stand in their room. */
export interface ArObject {
  /** goes in the page address (?ar=object_2), so a phone that scans the QR code opens on the same chair */
  id: string;
  /** the caption in its box */
  label: string;
  /** what the picture shows, for screen readers */
  alt: string;
  /** the picture in the boxes — and in the big box until the 3D model has arrived */
  image: string;
  /** the 3D model (.glb): what turns in the big box, and what the phone puts in the room */
  model: string;
  /** a ready-made .usdz for iPhones; without one the viewer converts the .glb on the phone, which only Safari can do */
  iosModel?: string;
  /** colour of the box behind the chair */
  backdrop: string;
  /**
   * where the picture was taken from, as the viewer's camera orbit "theta phi radius": the 3D view
   * opens on the same angle, with the chair the same size, so the model takes over from the picture unnoticed
   */
  orbit: string;
  /**
   * shown in the price box when the chair is tapped. PLACEHOLDERS: these three are not products
   * on littlenap.in, so the site has no price for them — replace with the real ones
   */
  price: string;
  mrp: string;
}

/**
 * The chairs of the AR section, in the order of the strip. Each one is a
 * picture `<id>.png` in public/assets/ar plus a design with the same name in
 * hero/model/recliners.ts — `npm run ar` makes the .webp and .glb from those.
 * They are finishes of one chair, described by what the picture shows.
 */
export const AR_OBJECTS: ArObject[] = [
  {
    id: 'object_1',
    label: 'Charcoal fabric',
    alt: 'Recliner in charcoal grey fabric with pillow-top arms',
    image: 'assets/ar/object_1.webp',
    model: 'assets/ar/object_1.glb',
    backdrop: '#d8d2c9',
    orbit: '-28deg 76deg 100%',
    price: '₹32,999',
    mrp: '₹49,999',
  },
  {
    id: 'object_2',
    label: 'Maroon leatherette',
    alt: 'Recliner in maroon leatherette with diamond quilting and a cup holder in each arm',
    image: 'assets/ar/object_2.webp',
    model: 'assets/ar/object_2.glb',
    backdrop: '#ead9ce',
    orbit: '0deg 79deg 95%',
    price: '₹38,999',
    mrp: '₹55,999',
  },
  {
    id: 'object_3',
    label: 'Tan leatherette',
    alt: 'Recliner in tan leatherette with a cup holder in each arm',
    image: 'assets/ar/object_3.webp',
    model: 'assets/ar/object_3.glb',
    backdrop: '#e6dccb',
    orbit: '-28deg 76deg 100%',
    price: '₹36,999',
    mrp: '₹52,999',
  },
];

export interface Product {
  name: string;
  type: string;
  /** two short lines about the chair, from its page on littlenap.in */
  blurb: string;
  price: string;
  mrp: string;
  off: string;
  image: string;
  href: string;
  /** stars out of five and the number of reviews on its listing — left out when it has none */
  rating?: { stars: number; reviews: number };
}

export const PRODUCTS: Product[] = [
  {
    name: 'Helios – Swivel Glider',
    blurb: 'Teak wood frame and plush suede, with a smooth recline up to 145°.',
    type: 'Manual · 360° swivel',
    price: '₹24,999',
    mrp: '₹41,665',
    off: '40% off',
    image: 'assets/products/helios-swivel-glider.jpg',
    href: `${SITE}/living-room-recliners/helios-swivel-glider`,
  },
  {
    name: 'Quies – Swivel Headrest Motor',
    blurb: 'Six-layered seating with a head motor and full lumbar support.',
    type: 'Motorised · Swivel',
    price: '₹43,000',
    mrp: '₹71,667',
    off: '40% off',
    image: 'assets/products/quies-swivel-headrest-motor.jpg',
    href: `${SITE}/living-room-recliners/single_seater_quies`,
  },
  {
    name: 'Versatil',
    blurb: 'Motorised leatherette recliner with a removable, extendable footrest.',
    type: 'Motorised · Single seater',
    price: '₹52,700',
    mrp: '₹62,000',
    off: '15% off',
    image: 'assets/products/versatil.jpg',
    href: `${SITE}/living-room-recliners/versatil-recliner-motorized-single-seater`,
  },
  {
    name: 'Helios Premium Recliner Sofa',
    blurb: '2- and 3-seater manual recliner sofa on a teak wood frame.',
    type: 'Manual · Multi-seater',
    price: '₹39,999',
    mrp: '₹55,999',
    off: '29% off',
    image: 'assets/products/helios-premium-manual-recliner-sofa-mult.jpg',
    href: `${SITE}/living-room-recliners/linea-1787552650`,
  },
  {
    name: 'Livo',
    blurb: 'Swivel glider recliner with 360-degree glide and swivel.',
    type: 'Single seater',
    price: '₹39,100',
    mrp: '₹46,000',
    off: '15% off',
    image: 'assets/products/livo.jpg',
    href: `${SITE}/living-room-recliners/livo`,
  },
  {
    name: 'Amet',
    blurb: 'Leather recliner with a stunning design and utmost relaxation.',
    type: 'Single seater',
    price: '₹31,499',
    mrp: '₹52,499',
    off: '40% off',
    image: 'assets/products/amet.jpg',
    href: `${SITE}/living-room-recliners/amet`,
  },
  {
    name: 'Swift',
    blurb: 'Manual single-seater with complete lumbar and head support.',
    type: 'Manual · Single seater',
    price: '₹36,550',
    mrp: '₹43,000',
    off: '15% off',
    image: 'assets/products/swift.jpg',
    href: `${SITE}/living-room-recliners/swift-recliner-manual-single-seater`,
    rating: { stars: 4, reviews: 5 },
  },
  {
    name: 'Galaxy',
    blurb: 'More than a recliner: your own personal relaxation zone.',
    type: 'Single seater',
    price: '₹21,000',
    mrp: '₹34,998',
    off: '40% off',
    image: 'assets/products/galaxy.jpg',
    href: `${SITE}/living-room-recliners/galaxy-recliner`,
  },
];

export interface Testimonial {
  name: string;
  /** stars out of five */
  stars: number;
  text: string;
  date: string;
  /** the recliner the review is about, and its page — where the review can be read */
  product: string;
  href: string;
}

/**
 * What customers say: reviews copied word for word from the "Top Reviews" on
 * the product pages of littlenap.in (read on 3 October 2026), with the names,
 * dates and stars shown there. Add only real ones — never write one.
 */
export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Simran Verma',
    stars: 5,
    text: 'The memory foam cushions on this recliner contour to my body perfectly.',
    date: '29 Apr 2024',
    product: 'Avion',
    href: `${SITE}/living-room-recliners/avion-recliner`,
  },
  {
    name: 'Arya Singh',
    stars: 5,
    text: 'Finally found a recliner that supports my back and looks great too!',
    date: '29 Mar 2024',
    product: 'Swift',
    href: `${SITE}/living-room-recliners/swift-recliner-manual-single-seater`,
  },
  {
    name: 'Vivaan Shetty',
    stars: 4,
    text: 'My whole family fights over who gets to sit in this luxurious lounger first!',
    date: '2 Apr 2024',
    product: 'Otium',
    href: `${SITE}/living-room-recliners/otium-recliner-manual-single-seater`,
  },
  {
    name: 'Rahul Desai',
    stars: 5,
    text: 'The reclining feature on this recliner is so smooth – it’s a pleasure to use.',
    date: '12 May 2024',
    product: 'Versatil',
    href: `${SITE}/living-room-recliners/versatil-recliner-motorized-single-seater`,
  },
  {
    name: 'Neha Joshi',
    stars: 5,
    text: 'The reclining function is seamless, and the padding is just right. Great purchase!',
    date: '19 Mar 2024',
    product: 'Livo',
    href: `${SITE}/living-room-recliners/livo`,
  },
  {
    name: 'Harsh Chauhan',
    stars: 4,
    text: 'I’ve never experienced such personalized attention to detail – this recliner was made just for me.',
    date: '29 Apr 2024',
    product: 'Avion',
    href: `${SITE}/living-room-recliners/avion-recliner`,
  },
  {
    name: 'Abhay Sinha',
    stars: 5,
    text: 'The compact size of this lounger makes it perfect for my small apartment.',
    date: '3 May 2024',
    product: 'Amet',
    href: `${SITE}/living-room-recliners/amet`,
  },
  {
    name: 'Nitish Chopra',
    stars: 5,
    text: 'I am in love with this recliner. Thank you Little-Nap',
    date: '15 Mar 2024',
    product: 'Quotium',
    href: `${SITE}/living-room-recliners/quotium-black-recliner-manual-single-seater`,
  },
  {
    name: 'Tara Pillai',
    stars: 5,
    text: 'Investing in this recliner was one of the best decisions I’ve made for my home.',
    date: '2 Apr 2024',
    product: 'Otium',
    href: `${SITE}/living-room-recliners/otium-recliner-manual-single-seater`,
  },
  {
    name: 'Arjun Rao',
    stars: 4,
    text: 'The perfect addition to my living room – this recliner screams luxury!',
    date: '28 Mar 2024',
    product: 'Swift',
    href: `${SITE}/living-room-recliners/swift-recliner-manual-single-seater`,
  },
  {
    name: 'Ritika Roy',
    stars: 4,
    text: 'This recliner has become my go-to spot for reading – the ergonomic design is perfect for long hours of sitting.',
    date: '2 May 2024',
    product: 'Amet',
    href: `${SITE}/living-room-recliners/amet`,
  },
  {
    name: 'Anika Menon',
    stars: 5,
    text: 'The quality craftsmanship of this recliner is evident in every detail.',
    date: '3 Apr 2024',
    product: 'Otium',
    href: `${SITE}/living-room-recliners/otium-recliner-manual-single-seater`,
  },
  {
    name: 'Raj Malhotra',
    stars: 4,
    text: 'I’ve never been happier with a purchase – this lounger has exceeded all my expectations.',
    date: '28 Apr 2024',
    product: 'Avion',
    href: `${SITE}/living-room-recliners/avion-recliner`,
  },
  {
    name: 'Aanya Reddy',
    stars: 5,
    text: 'Impressed by the quality and comfort of this recliner – highly recommend!',
    date: '28 Mar 2024',
    product: 'Swift',
    href: `${SITE}/living-room-recliners/swift-recliner-manual-single-seater`,
  },
  {
    name: 'Swati Goyal',
    stars: 4,
    text: 'I’ve never felt more supported than when I’m sitting in this ergonomic lounger.',
    date: '11 May 2024',
    product: 'Versatil',
    href: `${SITE}/living-room-recliners/versatil-recliner-motorized-single-seater`,
  },
];

export interface CoreValue {
  title: string;
  /** path data for a 24×24 stroke icon */
  icon: string;
}

export const CORE_VALUES: CoreValue[] = [
  {
    title: 'Precision Engineering',
    icon: 'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0-13v3m0 13v3m9.5-9.5h-3m-13 0h-3m16.2-6.7-2.1 2.1M7.4 16.6l-2.1 2.1m13.4 0-2.1-2.1M7.4 7.4 5.3 5.3',
  },
  {
    title: 'Quality Assurance',
    icon: 'M12 2.5 4.5 5.5v6c0 4.6 3.1 8.3 7.5 10 4.4-1.7 7.5-5.4 7.5-10v-6L12 2.5Zm-3.2 9.4 2.3 2.3 4.3-4.6',
  },
  {
    title: 'Design Innovation',
    icon: 'M9 18.5h6m-5 3h4M12 2.5a6.5 6.5 0 0 0-3.8 11.8c.5.4.8 1 .8 1.7h6c0-.7.3-1.3.8-1.7A6.5 6.5 0 0 0 12 2.5Z',
  },
  {
    title: 'Export Worldwide',
    icon: 'M12 21.5a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19Zm-9.5-9.5h19M12 2.5c2.5 2.6 3.8 5.8 3.8 9.5s-1.3 6.9-3.8 9.5c-2.5-2.6-3.8-5.8-3.8-9.5s1.3-6.9 3.8-9.5Z',
  },
  {
    title: 'Pan India Sales and Services',
    icon: 'M12 21.5s7-6.2 7-11.7a7 7 0 1 0-14 0c0 5.5 7 11.7 7 11.7Zm0-9.2a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2Z',
  },
  {
    title: 'Made to Order',
    icon: 'm3.5 16.5 13-13 4 4-13 13h-4v-4Zm10-10 4 4M6 4.5v4m-2-2h4m10 9v4m-2-2h4',
  },
];

export const PHILOSOPHY: string[] = [
  'Being compelled by a vision to redefine lifestyle, and consumed by the passion to create an ultra-comfortable experience, we at Little Nap always strive to build recliners that are relaxing for mind, body and soul.',
  'We have always been inspired by the wisdom behind natural sleep. A sweet “little”, comfortable “nap” that is completely refreshing — we engineer our recliners with a comfort that instantly puts one into napping mode.',
  'Our logo reflects a chair in reclining position. We always wanted to give maximum comfort the way the client chooses it. And this is why we customise our recliners to the last detail.',
];

export interface FooterColumn {
  title: string;
  links: NavLink[];
}

export const FOOTER: FooterColumn[] = [
  {
    title: 'Recliners',
    links: [
      { label: 'Lounger Recliners', href: `${SITE}/lounger` },
      { label: 'Living Room Recliners', href: `${SITE}/living-room-recliners` },
      { label: 'Home Theater Recliners', href: `${SITE}/home-theater-recliners` },
      { label: 'Multiplex Recliners', href: `${SITE}/multiplex-recliners` },
      { label: 'La-Z-Boy', href: `${SITE}/lazboy` },
    ],
  },
  {
    title: 'About Little Nap',
    links: [
      { label: 'Our USP', href: `${SITE}/our-usp` },
      { label: 'The Company', href: `${SITE}/the-company` },
      { label: 'Career', href: `${SITE}/careers` },
      { label: 'Franchise', href: `${SITE}/franchise` },
      { label: 'Awards and Recognition', href: `${SITE}/awards` },
      { label: 'Locate A Store', href: `${SITE}/showroom` },
    ],
  },
  {
    title: 'Help',
    links: [
      { label: 'Contact Us', href: `${SITE}/contact` },
      { label: 'FAQs', href: `${SITE}/faqs` },
      { label: 'Return', href: `${SITE}/return-policy` },
      { label: 'Download E-Catalogue', href: `${SITE}/download` },
      { label: 'Privacy Policy', href: `${SITE}/privacy-policy` },
      { label: 'Terms of Use', href: `${SITE}/term-use` },
    ],
  },
];

export const MARKETPLACES = ['Amazon', 'Flipkart', 'Pepperfry', 'Urban Ladder'];

/**
 * The franchise section, from littlenap.in/franchise (read on 3 October 2026):
 * the cities are the pins on its map ("Little Nap Franchises Available
 * Nationwide"), and the space and investment are those on the banner at the
 * top of that page.
 */
export const FRANCHISE = {
  cities: [
    'Chandigarh',
    'Delhi',
    'Lucknow',
    'Ahmedabad',
    'Surat',
    'Mumbai',
    'Nagpur',
    'Hyderabad',
    'Pune',
    'Kolkata',
    'Chennai',
    'Coimbatore',
  ],
  terms: [
    { label: 'Showroom space', value: '1,500–2,500 sq ft' },
    { label: 'Investment', value: '₹35–55 lacs' },
  ],
  /** the enquiry form for would-be franchisees, and the page that tells them more */
  apply: `${SITE}/franchise-form`,
  more: `${SITE}/franchise`,
};

export interface AppStore {
  id: 'apple' | 'google';
  /** the small line over the store's name */
  caption: string;
  name: string;
  /** the app's page in that store — empty until the app is published there */
  href: string;
}

/**
 * The mobile app's pages in the two stores. littlenap.in links to no app
 * (checked on 3 October 2026), so both are empty and the section says "coming
 * soon" — put the links in here when the app is out and that line goes away.
 */
export const APP_STORES: AppStore[] = [
  { id: 'apple', caption: 'Download on the', name: 'App Store', href: '' },
  { id: 'google', caption: 'Get it on', name: 'Google Play', href: '' },
];

export interface Payment {
  name: string;
  image: string;
  width: number;
  height: number;
}

/** Ways to pay: the logos littlenap.in's footer shows under "Securely Pay Using". */
export const PAYMENTS: Payment[] = [
  { name: 'UPI', image: 'assets/pay/upi.webp', width: 184, height: 120 },
  { name: 'Visa', image: 'assets/pay/visa.webp', width: 236, height: 148 },
  { name: 'Mastercard', image: 'assets/pay/mastercard.webp', width: 240, height: 152 },
];

export interface VideoItem {
  /** the YouTube video that opens when the card is played */
  id: string;
  /** the picture shown on the card */
  thumb: string;
  tag: string;
  title: string;
  text: string;
}

/**
 * Littlenap.in's own brand film (the one on its home page). It is the ONLY real
 * video we have: every card below plays it, until the client supplies more.
 * Give a card its own `id` (the part after `watch?v=`) when a real film exists.
 */
const BRAND_FILM = 'YnuImLY8emQ';

export const VIDEO_FEATURED: VideoItem = {
  id: BRAND_FILM,
  thumb: 'assets/misc/hero-fallback.jpg',
  tag: 'Brand film',
  title: 'A sweet “little”, comfortable “nap”',
  text: 'The Little Nap story in one film: recliners built to put you into napping mode, made in India and customised to the last detail.',
};

/** Small cards under the featured film. Titles are the site's own category names. */
export const VIDEOS: VideoItem[] = [
  {
    id: BRAND_FILM,
    thumb: 'assets/videos/t1.jpg',
    tag: 'Living Room',
    title: 'Living room recliners',
    text: 'Recliner sofas and chairs for the room your family lives in.',
  },
  {
    id: BRAND_FILM,
    thumb: 'assets/videos/t2.jpg',
    tag: 'Home Theater',
    title: 'Home theater seating',
    text: 'Two-seater cinema recliners for a private screening room at home.',
  },
  {
    id: BRAND_FILM,
    thumb: 'assets/videos/t3.jpg',
    tag: 'Lounger',
    title: 'Loungers',
    text: 'One chair, fully reclined, for the nap you have earned.',
  },
  {
    id: BRAND_FILM,
    thumb: 'assets/videos/t4.jpg',
    tag: 'Recliners',
    title: 'Single-seater recliners',
    text: 'Compact recliners that fit a corner, a bedroom or a study.',
  },
];
